import GlobalTable from '@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue';
import navbar from "@/components/probaworkflows_base_components/Navbar/NavBar.vue";
import ProbaWorkflowsSidebar from "@/components/probaworkflows_base_components/ProbaWorklfowsSidebar/ProbaWorkflowsSidebar.vue";
import { ACTIVATE_URL, ASSIGN_USER_TO_PROJECT, CREATE_USER_URLS, DEACTIVATE_URL, GET_COMPANIES, get_headers, get_token, GET_USERS_ASSIGNED_TO_PROJECT, GET_USERS_AUDIT_LOGS, normalize_user_role, ROLES_URL, UPDATE_USER_URL, USER_LIST_URLS } from '@/utils/constants/constants';
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import { validate_phone_number } from '@/utils/validations/validate_inputs';
import UserApplicationLogs from '@/views/global/management/user_activity_logs/UserActivityLogs.vue';
import axios from 'axios';
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import Swal from 'sweetalert2';

export default {
  name: "UserManagement",
  components: {
    GlobalTable,
    ProbaWorkflowsSidebar,
    navbar,
    UserApplicationLogs,
  },
  mixins: [alert_mixin, formatting_mixins],
  data: () => ({
    selected_user_id: null,
    selected_log_status: '',
    show_log_modal: false,
    application_logs: [],
    user_logs: [],
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    role: '',
    error_message: '',
    user_roles: [],
    users_list: [],
    table_headers: [],
    token: get_token(),
    headers: get_headers(),
    show_modal: false,
    users: [],
    roles: [],
    edit_user_data: {
      user_id: null,
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      role: ''
    },
    user_management_config: {
      icon_map: {
        dashboard: "fa-user",
        compliance: "fa-user-shield",
        pending_application: "bi bi-shield-shaded",
        logout: "fa-sign-out-alt"
      },
      text_map: {
        dashboard_title: "Management",
        dashboard: "Users",
        compliance: "Roles",
        pending_application: "Permission",
        logout: "Logout"
      }
    },
    timeout_id: null,
    is_loading_logs: false,
    project_list: [],
    project_headers: [],
    assigned_projects: [],
    user_id: null,
    project_id: null,
  }),

  methods: {

    async open_log_modal(row) {
      const user_id = row.user_id || row._rawApplicationData?.user_id;
      if (!user_id) return;

      this.selected_user_id = user_id;
      this.is_loading_logs = true;
      this.application_logs = [];

      try {
        const { data } = await axios.get(GET_USERS_AUDIT_LOGS, {
          headers: this.headers,
          params: { user_id }
        });

        if (data.status === 'success') {
          this.application_logs = data.data.map(log => ({
            ...log,
            timestamp: this.format_date(log.timestamp)
          }));
        } else {
          this.show_error(data.message || 'Failed to fetch user logs.');
        }
      } catch (err) {
        this.show_error(err.response?.data?.message || 'Unexpected error fetching logs.');
      } finally {
        this.is_loading_logs = false;
        this.show_log_modal = true;
      }
    },

    validate_phone_number(phone_number) {
      return validate_phone_number(phone_number)
    },

    add_user() {
      this.show_modal = true;
    },

    close_log_modal() {
      this.show_log_modal = false;
      this.selected_user_id = null;
    },

    async submit_user() {
      const payload = {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        phone_number: this.phone_number,
        role: this.role,
      };

      try {
        const response = await axios.post(CREATE_USER_URLS, payload, {
          headers: this.headers,
        });

        if (response.data.status === 'success') {
          this.user_id = response.data.data.id;
          if (this.project_id) {
            this.toggle_assignment(this.project_id);
          }
          this.show_success('User has been successfully created.');
          window.location.reload();

        }
      } catch (error) {
        let message = "Error creating user, ensure all details are provided correctly.";

        if (error.response && error.response.data) {
          const data = error.response.data;

          const parsed = typeof data === "string" ? JSON.parse(data) : data;

          if (parsed.message) {
            message = parsed.message;
          } else if (parsed.data) {
            if (typeof parsed.data === "string") {
              message = parsed.data;
            } else if (typeof parsed.data === "object") {
              message = Object.values(parsed.data).flat().join(", ");
            }
          }

        }

        this.show_error(message);
      }

    },

    edit_user(user) {
      const name_parts = user.name.split(' ');
      const matching_role = this.user_roles.find(r => r.role === user.role);
      this.edit_user_data = {
        user_id: user.user_id,
        first_name: name_parts[0],
        last_name: name_parts[1] || '',
        email: user.email,
        phone_number: user.phone_number || '',
        role: user.role,
        role_id: matching_role ? matching_role.id : null
      };
    },

    async toggle_user_status(user) {
      const id = user.user_id;
      const action = user.is_active ? 'deactivate' : 'activate';
      const url = user.is_active ? DEACTIVATE_URL : ACTIVATE_URL;

      const result = await Swal.fire({
        title: `Are you sure?`,
        text: `This will ${action} the user.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${action}`,
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const { data } = await axios.post(
          url,
          { user_id: id },
          { headers: this.headers }
        );

        if (data.status === 'success') {
          this.show_success(`User ${action}d successfully.`);
          user.is_active = !user.is_active;
          this.fetch_users();
        } else {
          this.show_error(data.message || 'An error occurred.');
        }
      } catch (err) {
        this.show_error(
          err.response?.data?.message ||
          err.message ||
          'Unexpected error'
        );
      }
    },

    async update_user() {
      const payload = {
        user_id: this.edit_user_data.user_id,
        email: this.edit_user_data.email,
        first_name: this.edit_user_data.first_name,
        last_name: this.edit_user_data.last_name,
        phone_number: this.edit_user_data.phone_number,
        role_id: this.edit_user_data.role_id,
      };

      await axios.post(
        UPDATE_USER_URL,
        payload,
        { headers: this.headers }
      ).then(({ data }) => {
        if (data.status === 'success') {
          const current_user_id = sessionStorage.getItem('user_id');
          if (String(this.edit_user_data.user_id) === String(current_user_id)) {
            sessionStorage.setItem('user_role', normalize_user_role(data.data.role));
            sessionStorage.setItem('lead_name',`${data.data.first_name} ${data.data.last_name}`);
            window.dispatchEvent(new Event('session-storage-updated'));
          }
          this.show_success(data.message);
          this.fetch_users();
        } else {
          this.show_error(`Error updating user: ${data.message}`);
        }
      }).catch((error) => {
        const backend_msg =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message;

        this.show_error(`Error updating user: ${backend_msg}`);
      });
    },

    async fetch_users() {
      this.error_message = '';
      let project_id = this.project_id;
      try {
        let response;

        const payload = {
          page_size: 1000
        };

        if (project_id >= 0) {
          payload.project_id = project_id;
        }

        response = await axios.get(
          USER_LIST_URLS,
          {
            headers: this.headers,
            params: payload
          }
        );

        const res = JSON.parse(response.data);

        this.users = res.data.map(user => ({
          user_id: user.user_id,
          name: (user.user.first_name && user.user.last_name)
            ? user.user.first_name + " " + user.user.last_name
            : "User has not logged in",
          email: user.user.email,
          phone_number: user.phone_number || '',
          last_login: this.format_date(user.user.last_login ?? null, "Never logged in"),
          role: user.user.role.role,
          is_active: user.is_active,
        }));

        this.users_list = this.users;
        this.table_headers = Object.keys(this.users_list[0]).map(header =>
        this.format_question_type(header)
      );
        this.initializeTable();
      } catch (error) {
        this.error_message = 'Error fetching users: ' + error.message;
      }
    },

    async fetch_roles() {
      try {
        const response = await axios.get(ROLES_URL, {
          headers: this.headers,
        });
        this.user_roles = response.data.data;
      } catch (error) {
        this.show_error('Error fetching roles: ' + error.message);
      }
    },

    get_projects() {
      this.project_list = [];
      return this.show_loader("Loading companies...")
        .then(loader => {
          return axios.get(GET_COMPANIES, {
            headers: this.headers
          })
            .then(response => {
              const parsed_response = JSON.parse(response.data);
              if (parsed_response.data && parsed_response.data.length > 0) {
                const projects = parsed_response.data;
                this.project_list = projects.map(project => ({
                  id: project.id,
                  name: project.name,
                  description: project.project_description,
                }));
                this.project_headers = Object.keys(this.project_list[0])
              }

              return this.close_loader(loader);
            })
            .catch(error => {
              this.show_error('Error getting companies: ' + error);
              return this.close_loader(loader);
            });
        });
    },

    async select_user(user_id) {
      this.assigned_projects = [];
      this.user_id = user_id;

      const payload = {
        user_id: this.user_id,
        page_size: 1000
      };

      try {
        const response = await axios.post(GET_USERS_ASSIGNED_TO_PROJECT, payload, { headers: this.headers });
        const res = response.data;

        if (res.data && Array.isArray(res.data)) {
          this.assigned_projects = res.data.map(item => item.project);
        }
      } catch (error) {
        this.show_error('Error fetching assigned projects: ' + error.message);
      }
    },

    toggle_assignment(project_id) {
      const payload = {
        project_id: project_id,
        user_id: this.user_id
      };

      if (this.assigned_projects.includes(project_id)) {
        payload.is_assigned = false;
      } else {
        payload.is_assigned = true;
      }

      axios.post(ASSIGN_USER_TO_PROJECT, payload, { headers: this.headers })
        .then(() => {
          if (payload.is_assigned) {
            this.assigned_projects.push(project_id);
          } else {
            this.assigned_projects = this.assigned_projects.filter(id => id !== project_id);
          }
        })
        .catch(() => {
          this.show_error("Error fetching projects");
        });
    },

    go_back_to_company_management() {
      this.$router.push('/company_management');
    },

    validate_numbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
        return false;
      }
      return true;
    }
  },
  async mounted() {
    this.project_id = sessionStorage.getItem('project_id') || null;
    await this.fetch_users();
    await this.fetch_roles();
    await this.get_projects();


  },
  beforeDestroy() {
    if (this.timeout_id) {
      clearTimeout(this.timeout_id);
    }
  }
};
